/**
 * oc-admin-api.ts
 * Thin fetch wrapper for all /api/admin/* endpoints.
 * Mirrors the ocFetch pattern from the tenant portal.
 */

const OC_ADMIN_API_BASE = import.meta.env.VITE_OC_ADMIN_API_BASE ?? "/api/admin";

export const OC_ADMIN_SESSION_KEY = "oc_admin_session_token";
const OC_ADMIN_SESSION_CREATED_KEY = "oc_admin_session_created";
const OC_ADMIN_LAST_ACTIVITY_KEY = "oc_admin_last_activity";

export const OC_ADMIN_SESSION_MAX_MS = 24 * 60 * 60 * 1000;   // 24 hours
export const OC_ADMIN_INACTIVITY_MAX_MS = 8 * 60 * 60 * 1000; // 8 hours

export function ocAdminGetSessionToken(): string | null {
  return localStorage.getItem(OC_ADMIN_SESSION_KEY);
}

export function ocAdminSetSessionToken(token: string): void {
  const now = String(Date.now());
  localStorage.setItem(OC_ADMIN_SESSION_KEY, token);
  localStorage.setItem(OC_ADMIN_SESSION_CREATED_KEY, now);
  localStorage.setItem(OC_ADMIN_LAST_ACTIVITY_KEY, now);
}

export function ocAdminClearSessionToken(): void {
  localStorage.removeItem(OC_ADMIN_SESSION_KEY);
  localStorage.removeItem(OC_ADMIN_SESSION_CREATED_KEY);
  localStorage.removeItem(OC_ADMIN_LAST_ACTIVITY_KEY);
}

export function ocAdminRecordActivity(): void {
  localStorage.setItem(OC_ADMIN_LAST_ACTIVITY_KEY, String(Date.now()));
}

export function ocAdminIsSessionExpired(): boolean {
  const now = Date.now();
  const created = Number(localStorage.getItem(OC_ADMIN_SESSION_CREATED_KEY) ?? "0");
  const lastActivity = Number(localStorage.getItem(OC_ADMIN_LAST_ACTIVITY_KEY) ?? created);
  if (created > 0 && now - created > OC_ADMIN_SESSION_MAX_MS) return true;
  if (now - lastActivity > OC_ADMIN_INACTIVITY_MAX_MS) return true;
  return false;
}

export class OcAdminApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "OcAdminApiError";
  }
}

interface OcFetchOptions extends RequestInit {
  /** Skip the /api/admin prefix (use full URL instead) */
  raw?: boolean;
}

export async function ocAdminFetch<T>(path: string, options: OcFetchOptions = {}): Promise<T> {
  const { raw, ...init } = options;
  const url = raw ? path : `${OC_ADMIN_API_BASE}${path}`;

  const sessionToken = ocAdminGetSessionToken();
  const res = await fetch(url, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      ...(sessionToken ? { "X-OC-Admin-Session-Token": sessionToken } : {}),
      ...init.headers,
    },
  });

  if (!res.ok) {
    let code = "UNKNOWN_ERROR";
    let message = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      code = body?.error?.code ?? code;
      message = body?.error?.message ?? message;
    } catch {
      // ignore JSON parse failure
    }
    throw new OcAdminApiError(res.status, code, message);
  }

  // 204 No Content
  if (res.status === 204) return undefined as unknown as T;

  return res.json() as Promise<T>;
}

// ---------------------------------------------------------------------------
// Tenants
// ---------------------------------------------------------------------------

export interface OcTenantSummary {
  id: string;
  slug: string;
  name: string;
  plan: "founder" | "growth" | "enterprise";
  status: "active" | "suspended" | "trial" | "churned";
  studentCount: number;
  agentStatus: "healthy" | "degraded" | "offline";
  successfulJobs24h: number;
  failedJobs24h: number;
  createdAt: string;
}

export interface OcTenantDetail extends OcTenantSummary {
  contactEmail: string;
  adDomain: string;
  slateDomain: string;
  contractStartDate: string | null;
  contractEndDate: string | null;
  acv: number | null;
  founderSlot: boolean;
  notes: string;
}

export interface OcTenantUsage {
  tenantId: string;
  name: string;
  planTier: string;
  status: string;
  isTrial: boolean;
  endsAt: string | null;
  isFounder: boolean;
  founderDiscountPct: number | null;
  studentsPerYearLimit: number | null;
  currentYear: number;
  studentsCreatedThisYear: number;
  usageHistory: { year: number; studentsCreated: number }[];
}

export interface OcTenantUser {
  id: string;
  email: string;
  name: string;
  status: "active" | "invited" | "suspended";
  role: string;
  lastLoginAt: string | null;
  invitedAt: string | null;
  createdAt: string;
}

export interface OcProvisioningEvent {
  id: string;
  triggerEvent: string;
  status: "queued" | "processing" | "success" | "failed" | "retrying";
  attempts: number;
  lastError: string | null;
  createdAt: string;
  studentName: string | null;
  studentEmail: string | null;
}

export interface OcTenantCreateBody {
  name: string;
  slug: string;
  planTier?: string;
  isFounder?: boolean;
  cloudflareAccessAud?: string;
  endsAt?: string;
  leadId?: string;
}

export const ocAdminTenantsApi = {
  list: () => ocAdminFetch<OcTenantSummary[]>("/tenants"),
  create: (body: OcTenantCreateBody) =>
    ocAdminFetch<{ id: string; slug: string; name: string }>("/tenants", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  get: (tenantId: string) => ocAdminFetch<OcTenantDetail>(`/tenants/${tenantId}`),
  update: (tenantId: string, body: Partial<OcTenantDetail>) =>
    ocAdminFetch<OcTenantDetail>(`/tenants/${tenantId}`, { method: "PATCH", body: JSON.stringify(body) }),
  suspend: (tenantId: string) =>
    ocAdminFetch<OcTenantDetail>(`/tenants/${tenantId}`, {
      method: "PATCH",
      body: JSON.stringify({ status: "suspended" }),
    }),
  reinstate: (tenantId: string) =>
    ocAdminFetch<OcTenantDetail>(`/tenants/${tenantId}`, {
      method: "PATCH",
      body: JSON.stringify({ status: "active" }),
    }),
  impersonate: (tenantId: string, targetUserEmail: string, reason: string) =>
    ocAdminFetch<{ token: string; sessionId: string; expiresIn: number; tenantId: string; targetUserEmail: string }>(
      "/impersonate",
      { method: "POST", body: JSON.stringify({ tenantId, targetUserEmail, reason }) }
    ),
  getUsage: (id: string) =>
    ocAdminFetch<OcTenantUsage>(`/tenants/${id}/usage`),
  getEvents: (id: string) =>
    ocAdminFetch<OcProvisioningEvent[]>(`/tenants/${id}/events`),
  getUsers: (id: string) =>
    ocAdminFetch<OcTenantUser[]>(`/tenants/${id}/users`),
  updateLimit: (id: string, body: {
    studentsPerYearLimit?: number | null;
    planTier?: string;
    isFounder?: boolean;
    founderDiscountPct?: number | null;
    endsAt?: string | null;
    status?: string;
  }) =>
    ocAdminFetch<{ tenant: Record<string, unknown> }>(`/tenants/${id}/limit`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
};

// ---------------------------------------------------------------------------
// System Health
// ---------------------------------------------------------------------------

export interface OcPlatformMetrics {
  queueDepth: number;
  errorRatePct: number;
  d1LatencyMs: number;
  r2StorageGb: number;
  capturedAt: string;
}

export interface OcTenantHealthRow {
  tenantId: string;
  tenantName: string;
  agentStatus: "healthy" | "degraded" | "offline";
  failedJobs24h: number;
  healthScore: "green" | "amber" | "red";
}

export interface OcActiveAlert {
  id: string;
  severity: "critical" | "warning" | "info";
  message: string;
  tenantId: string | null;
  createdAt: string;
}

export interface OcHealthHistory {
  date: string;
  errorRatePct: number;
  queueDepth: number;
  d1LatencyMs: number;
}

export const ocAdminHealthApi = {
  platformMetrics: () => ocAdminFetch<OcPlatformMetrics>("/health/platform"),
  tenantHealth: () => ocAdminFetch<OcTenantHealthRow[]>("/health/tenants"),
  activeAlerts: () => ocAdminFetch<OcActiveAlert[]>("/health/alerts"),
  history7d: () => ocAdminFetch<OcHealthHistory[]>("/health/history"),
  dismissAlert: (alertId: string) =>
    ocAdminFetch<void>(`/health/alerts/${alertId}/dismiss`, { method: "POST" }),
};

// ---------------------------------------------------------------------------
// Leads
// ---------------------------------------------------------------------------

export type OcLeadStatus =
  | "new"
  | "contacted"
  | "demo_scheduled"
  | "qualified"
  | "closed_won"
  | "closed_lost";

export interface OcLead {
  id: string;
  institutionName: string;
  contactName: string;
  contactTitle: string;
  email: string;
  phone: string | null;
  status: OcLeadStatus;
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

export interface OcLeadCreateBody {
  institutionName: string;
  contactName: string;
  contactTitle: string;
  email: string;
  phone?: string;
  utmSource?: string;
  notes?: string;
}

export const ocAdminLeadsApi = {
  list: () => ocAdminFetch<OcLead[]>("/leads"),
  get: (leadId: string) => ocAdminFetch<OcLead>(`/leads/${leadId}`),
  create: (body: OcLeadCreateBody) =>
    ocAdminFetch<OcLead>("/leads", { method: "POST", body: JSON.stringify(body) }),
  updateStatus: (leadId: string, status: OcLeadStatus) =>
    ocAdminFetch<OcLead>(`/leads/${leadId}/status`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  update: (leadId: string, body: Partial<OcLead>) =>
    ocAdminFetch<OcLead>(`/leads/${leadId}`, { method: "PATCH", body: JSON.stringify(body) }),
  exportCsv: () => {
    const token = ocAdminGetSessionToken();
    return fetch(`${OC_ADMIN_API_BASE}/leads/export.csv`, {
      headers: token ? { "X-OC-Admin-Session-Token": token } : {},
    });
  },
  sendIntroEmail: (leadId: string) =>
    ocAdminFetch<void>(`/leads/${leadId}/send-intro`, { method: "POST" }),
  delete: (leadId: string) =>
    ocAdminFetch<{ ok: boolean }>(`/leads/${leadId}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export interface OcContract {
  id: string;
  tenantId: string;
  tenantName: string;
  startDate: string;
  endDate: string;
  acv: number;
  status: "draft" | "sent" | "viewed" | "signed" | "declined" | "expired";
  docUrl: string | null;
  createdAt: string;
}

export const ocAdminContractsApi = {
  list: (tenantId?: string) => ocAdminFetch<OcContract[]>(tenantId ? `/contracts?tenantId=${encodeURIComponent(tenantId)}` : "/contracts"),
  get: (contractId: string) => ocAdminFetch<OcContract>(`/contracts/${contractId}`),
  create: (body: { tenantId: string; startDate: string; endDate: string; acv: number; docUrl?: string; planTier?: string; studentsPerYearLimit?: number | null }) =>
    ocAdminFetch<OcContract>("/contracts", { method: "POST", body: JSON.stringify(body) }),
  sendForSignature: (contractId: string, recipientEmail: string) =>
    ocAdminFetch<OcContract>(`/contracts/${contractId}/send-for-signature`, {
      method: "POST",
      body: JSON.stringify({ recipientEmail }),
    }),
};

// ---------------------------------------------------------------------------
// Billing
// ---------------------------------------------------------------------------

export interface OcBillingSummary {
  totalMrr: number;
  totalArr: number;
  activeSubscriptions: number;
  pastDueCount: number;
}

export interface OcBillingRecord {
  id: string;
  tenantId: string;
  tenantName: string;
  amount: number;
  currency: string;
  status: "paid" | "past_due" | "pending" | "failed";
  dueDate: string;
  paidAt: string | null;
}

export const ocAdminBillingApi = {
  summary: () => ocAdminFetch<OcBillingSummary>("/billing/summary"),
  records: () => ocAdminFetch<OcBillingRecord[]>("/billing/records"),
};

// ---------------------------------------------------------------------------
// Founder Slots
// ---------------------------------------------------------------------------

export interface OcFounderSlot {
  id: string;
  tenantId: string;
  tenantName: string;
  contractDate: string;
  acv: number;
  studentCount: number;
  founderSlot: boolean;
}

export interface OcFounderSlotsMeta {
  claimed: number;
  total: number;
}

export const ocAdminFounderSlotsApi = {
  meta: () => ocAdminFetch<OcFounderSlotsMeta>("/founder-slots/meta"),
  list: () => ocAdminFetch<OcFounderSlot[]>("/founder-slots"),
  setFounderSlot: (tenantId: string, value: boolean) =>
    ocAdminFetch<void>(`/founder-slots/${tenantId}`, {
      method: "PATCH",
      body: JSON.stringify({ founderSlot: value }),
    }),
};

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

export interface OcCampaign {
  id: string;
  name: string;
  status: "draft" | "active" | "paused" | "completed";
  channel: "email" | "linkedin" | "outbound";
  sentCount: number;
  openRatePct: number | null;
  replyRatePct: number | null;
  createdAt: string;
}

export interface OcCampaignContact {
  id: string;
  status: "enrolled" | "active" | "completed" | "unsubscribed" | "bounced";
  currentStep: number;
  enrolledAt: string;
  leadId: string | null;
  name: string | null;
  email: string | null;
  institutionName: string | null;
}

export const ocAdminCampaignsApi = {
  list: () => ocAdminFetch<OcCampaign[]>("/campaigns"),
  listContacts: (campaignId: string) =>
    ocAdminFetch<OcCampaignContact[]>(`/campaigns/${campaignId}/contacts`),
  addContact: (campaignId: string, contact: { name: string; email: string; institutionName: string }) =>
    ocAdminFetch<{ ok: boolean; leadId: string }>(`/campaigns/${campaignId}/contacts`, {
      method: "POST",
      body: JSON.stringify(contact),
    }),
  importContacts: (campaignId: string, contacts: { name: string; email: string; institutionName: string }[]) =>
    ocAdminFetch<{ added: number; skipped: number; errors: string[] }>(`/campaigns/${campaignId}/contacts/import`, {
      method: "POST",
      body: JSON.stringify({ contacts }),
    }),
  sendEmail: (campaignId: string, contactId: string) =>
    ocAdminFetch<{ ok: boolean; stepSent: number; isComplete: boolean; emailMessageId: string | null }>(
      `/campaigns/${campaignId}/contacts/${contactId}/send`,
      { method: "POST" },
    ),
  updateContactStatus: (campaignId: string, contactId: string, status: OcCampaignContact["status"]) =>
    ocAdminFetch<{ ok: boolean; status: string }>(
      `/campaigns/${campaignId}/contacts/${contactId}`,
      { method: "PATCH", body: JSON.stringify({ status }) },
    ),
};

// ---------------------------------------------------------------------------
// Content — Blog
// ---------------------------------------------------------------------------

export interface OcBlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  status: "draft" | "published" | "archived";
  author: string;
  publishedAt: string | null;
  tags: string[];
}

export const ocAdminBlogApi = {
  list: () => ocAdminFetch<OcBlogPost[]>("/content/blog"),
  get: (postId: string) => ocAdminFetch<OcBlogPost>(`/content/blog/${postId}`),
};

// ---------------------------------------------------------------------------
// Content — Case Studies
// ---------------------------------------------------------------------------

export interface OcCaseStudy {
  id: string;
  slug: string;
  institutionName: string;
  headline: string;
  status: "draft" | "published";
  publishedAt: string | null;
}

export const ocAdminCaseStudiesApi = {
  list: () => ocAdminFetch<OcCaseStudy[]>("/content/case-studies"),
};

// ---------------------------------------------------------------------------
// Internal Users
// ---------------------------------------------------------------------------

export interface OcInternalUser {
  id: string;
  name: string;
  email: string;
  role: "owner" | "admin" | "support" | "viewer";
  lastLoginAt: string | null;
  createdAt: string;
}

export const ocAdminUsersApi = {
  list: () => ocAdminFetch<OcInternalUser[]>("/users"),
  invite: (email: string, role: OcInternalUser["role"]) =>
    ocAdminFetch<OcInternalUser>("/users/invite", {
      method: "POST",
      body: JSON.stringify({ email, role }),
    }),
  updateRole: (userId: string, role: OcInternalUser["role"]) =>
    ocAdminFetch<OcInternalUser>(`/users/${userId}/role`, {
      method: "PATCH",
      body: JSON.stringify({ role }),
    }),
  remove: (userId: string) =>
    ocAdminFetch<void>(`/users/${userId}`, { method: "DELETE" }),
};

// ---------------------------------------------------------------------------
// Resources
// ---------------------------------------------------------------------------

export const ocAdminResourcesApi = {
  getPricing:   () => ocAdminFetch<unknown>("/resources/pricing"),
  getHecvat:    () => ocAdminFetch<unknown>("/resources/hecvat"),
  getWorkflows: () => ocAdminFetch<unknown>("/resources/workflows"),
};

// ---------------------------------------------------------------------------
// Support
// ---------------------------------------------------------------------------

export interface OcSupportTicket {
  id: string;
  tenantId: string;
  tenantName: string | null;
  subject: string;
  body: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority: "low" | "normal" | "high" | "urgent";
  createdByEmail: string;
  assignedToEmail: string | null;
  resolvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OcSupportMessage {
  id: string;
  ticketId: string;
  senderType: "admin" | "tenant";
  senderEmail: string;
  body: string;
  isInternal: number;
  createdAt: string;
}

export const ocAdminSupportApi = {
  listTickets: (params?: { status?: string; tenantId?: string }) => {
    const qs = params ? new URLSearchParams(
      Object.fromEntries(Object.entries(params).filter(([, v]) => v != null)) as Record<string, string>
    ).toString() : "";
    return ocAdminFetch<{ tickets: OcSupportTicket[] }>(`/support/tickets${qs ? `?${qs}` : ""}`);
  },
  getTicket: (ticketId: string) =>
    ocAdminFetch<{ ticket: OcSupportTicket; messages: OcSupportMessage[] }>(`/support/tickets/${ticketId}`),
  reply: (ticketId: string, message: string, isInternal = false) =>
    ocAdminFetch<{ ok: boolean; messageId: string }>(`/support/tickets/${ticketId}/messages`, {
      method: "POST",
      body: JSON.stringify({ message, isInternal }),
    }),
  updateTicket: (ticketId: string, updates: { status?: string; priority?: string; assignedToEmail?: string }) =>
    ocAdminFetch<{ ticket: OcSupportTicket }>(`/support/tickets/${ticketId}`, {
      method: "PATCH",
      body: JSON.stringify(updates),
    }),
  createTicket: (payload: { tenantId: string; subject: string; body: string; priority: string; createdByEmail: string }) =>
    ocAdminFetch<{ ticket: OcSupportTicket }>(`/support/tickets`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
};
